import React, { ChangeEvent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useFetchPartnerVersions } from 'hooks/queries/useFetchPartnerVersions';
import { usePartnerDlmV2Enabled } from 'hooks/queries/usePartnerDlmV2Enabled';
import { Box, Grid, Table, TableBody, TablePagination, ThemeProvider } from '@mui/material';
import { GlobalState } from 'reducers/stateTypes';
import CustomDateRangePicker from 'components/Common/CustomDateRangePicker';
import { isNotNullOrUndefined } from 'utils/utils';
import moment from 'moment';
import { JBTheme } from '@bjb-gcrm/gcrm-frontend-components';
import { SkeletonTableRow } from 'components/Common/SkeletonTableRow';
import { stableSort } from 'utils/dataTableUtils';
import { TextFilter } from 'components/Common/TextFilter';
import { Employee } from 'types/Partner';
import { PartnerStatusDto } from 'views/RevisionHistory/Partner/PartnerRevisionDtos';
import { PartnerRevisionHistoryRow } from 'views/RevisionHistory/Partner/PartnerRevisionHistoryRow';
import { isVersionADraft, isVersionVisibleInRevisionHistory } from 'views/DraftMode/DraftSwitch';

function filterDateRange(dateRange: [Date | null, Date | null], sortedRevisionHistoryItems: any[]) {
  const dateFrom = moment.isDate(dateRange[0]) ? moment(dateRange[0]).toISOString() : null;
  //Add 24 hours to include the full 'to' date
  const dateTo = moment.isDate(dateRange[1]) ? moment(dateRange[1]).add(24, 'hours').toISOString() : null;
  return isNotNullOrUndefined(dateTo) && isNotNullOrUndefined(dateFrom)
    ? sortedRevisionHistoryItems?.filter(
        (item: PartnerStatusDto) => item.modifiedTimestamp >= dateFrom && item.modifiedTimestamp <= dateTo,
      )
    : sortedRevisionHistoryItems;
}

function filterSearchWord(searchWord: string, dateFiltered: PartnerStatusDto[], thRm: Employee[]) {
  if (searchWord === '') return dateFiltered;
  const lowercaseSearchWord = searchWord.toLowerCase();
  return dateFiltered.filter((row) => {
    const employee = thRm.find((employee) => employee.userId === row.modifiedBy);
    const searchRow = row.reason + ' ' + employee?.fullName;
    return searchRow.toLowerCase().includes(lowercaseSearchWord);
  });
}

const addLatestChangesItemIfEmptyHistory = (
  partnerStatusDtos: PartnerStatusDto[],
  techId: string,
): PartnerStatusDto[] => {
  if (partnerStatusDtos.length === 0) {
    const latestChangesWhenThereAreNoChanges: PartnerStatusDto = {
      partnerUid: techId,
      partnerStatus: 'ACTIVE', // this must be ACTIVE
      version: -1, // we skip based on this further down
      createdTimestamp: new Date().toISOString(),
      modifiedBy: 'unknown',
      modifiedTimestamp: new Date().toISOString(),
    };
    partnerStatusDtos.push(latestChangesWhenThereAreNoChanges);
  }
  return partnerStatusDtos;
};

const PartnerRevisionHistoryTable = ({ techId }: { techId: string }) => {
  const { t } = useTranslation();
  // DLM v2: the iframe no longer tells the portal about draft changes, so reload on every open.
  const isPartnerDlmV2 = usePartnerDlmV2Enabled();
  const { data: revisionHistoryItems = [], isFetching } = useFetchPartnerVersions(
    techId,
    isPartnerDlmV2 ? 'always' : false,
  );
  const thRm: Employee[] = useSelector((state: GlobalState) => state.partnerForm.thRm);
  const [dateRange, setDateRange] = useState<[null | Date, null | Date]>([null, null]);
  const [searchWord, setSearchWord] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const sortedRevisionHistoryItems: PartnerStatusDto[] = useMemo(() => {
    const partnerVersions: PartnerStatusDto[] = stableSort(revisionHistoryItems, (a, b) => b.version - a.version);
    const visibleVersions = partnerVersions.filter((rev) => isVersionVisibleInRevisionHistory(rev.partnerStatus));
    return addLatestChangesItemIfEmptyHistory(visibleVersions, techId);
  }, [revisionHistoryItems]);

  const filteredItems = useMemo(() => {
    const dateFiltered = filterDateRange(dateRange, sortedRevisionHistoryItems);
    return filterSearchWord(searchWord, dateFiltered, thRm);
  }, [dateRange, sortedRevisionHistoryItems, searchWord, thRm]);

  const pagedItems = useMemo(() => {
    return filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  if (isFetching) {
    return (
      <Box sx={{ width: '100%' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableBody>
            <SkeletonTableRow rowCount={5} cellCount={1} id="revision-table-skeleton" />
          </TableBody>
        </Table>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={JBTheme}>
      <Grid container direction="row" rowGap={1} gap={2}>
        <Grid item>
          <CustomDateRangePicker
            setSelectedDate={setDateRange}
            selectedDate={dateRange}
            disabled={false}
            useFormik={false}
            label={t('revisionHistory.dateRange')}
          />
        </Grid>
        <Grid item xs={4}>
          <TextFilter
            filterName={t('revisionHistory.search.label')}
            placeholderText={t('revisionHistory.search.placeholder')}
            id="revision-history-filter-input"
            dispatchAction={(val: string) => {
              setSearchWord(val);
            }}
          />
        </Grid>
        <Grid item xs={12} container rowGap={1}>
          {pagedItems.length > 0 && !isVersionADraft(pagedItems[0].partnerStatus) && (
            <Grid item xs={12} key={pagedItems[0].version + '-latest'}>
              <PartnerRevisionHistoryRow
                techId={techId}
                partnerStatusDtos={sortedRevisionHistoryItems}
                version={pagedItems[0].version}
                employees={thRm}
                dlmLightRowType={pagedItems[0].version >= 0 ? 'LATEST_CHANGES_NO_DRAFT' : 'LATEST_CHANGES_NO_VERSIONS'}
              />
            </Grid>
          )}
          {pagedItems
            .filter((p) => p.version >= 0)
            .map((history) => (
              <Grid item xs={12} key={history.version}>
                <PartnerRevisionHistoryRow
                  techId={techId}
                  partnerStatusDtos={sortedRevisionHistoryItems}
                  version={history.version}
                  employees={thRm}
                  dlmLightRowType={
                    isVersionADraft(history.partnerStatus) ? 'LATEST_CHANGES_WITH_DRAFT' : 'NORMAL_VERSION'
                  }
                />
              </Grid>
            ))}
        </Grid>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 20, 50]}
          count={sortedRevisionHistoryItems?.length}
          rowsPerPage={rowsPerPage}
          slotProps={{
            actions: {
              nextButton: {
                id: 'next-page-button',
              },
              previousButton: {
                id: 'previous-page-button',
              },
            },
          }}
          page={page}
          style={{ marginLeft: 'auto' }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Entries per page"
        />
      </Grid>
    </ThemeProvider>
  );
};

export default PartnerRevisionHistoryTable;
